"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.HDSModelItemsDefs = void 0;
const HDSItemDef_ts_1 = require("./HDSItemDef.js");
/**
 * ItemsDefs - Extension of HDSModel
 */
class HDSModelItemsDefs {
    /**
     * Model instance
     */
    #model;
    /**
     * ItemDefs Cache
     * KeyValue of itemsDefs
     */
    #itemsDefs;
    /**
     * get itemsData by streamId and eventType
     */
    #modelDataByStreamIdEventTypes;
    constructor(model) {
        this.#model = model;
        this.#itemsDefs = {};
        this.#modelDataByStreamIdEventTypes = {};
        loadModelDataByStreamIdEventTypes(this.#model.modelData.items, this.#modelDataByStreamIdEventTypes);
    }
    /**
     * get all itemDefs (includes deprecated). Use this when reading existing
     * events: every event must still resolve to an itemDef even if the item
     * was deprecated.
     */
    getAll() {
        const res = [];
        for (const key of Object.keys(this.#model.modelData.items)) {
            res.push(this.forKey(key));
        }
        return res;
    }
    /**
     * get all non-deprecated itemDefs a person enters field by field (`isEnterable`: no system, document or imported
     * item, nor a type this version does not know). Use this for any UI that lets a user pick an item to create new
     * data points (form builders, item picker sheets, data-model browser default listing). The others remain
     * resolvable via `forKey` / `forEvent` so existing events still render.
     */
    getAllActive() {
        return this.getAll().filter((itemDef) => !itemDef.isDeprecated && itemDef.isEnterable);
    }
    /**
     * The active item that asserts `code` in its `references.loinc` or `references.loincMethod` (method-specific codes,
     * data-model 3.32.0), or null. An item owns the LOINC codes it asserts (one owner per code over both lists,
     * data-model `[LABC-LOINC-1]`), so a `lab-result/coded-v1` event carrying one belongs on that item
     * (`checkLabResultCoded`).
     */
    forLoinc(code) {
        const asList = (codes) => Array.isArray(codes) ? codes : (codes != null ? [codes] : []);
        for (const itemDef of this.getAll()) {
            if (itemDef.isDeprecated)
                continue;
            const refs = itemDef.data.references;
            if (asList(refs?.loinc).includes(code) || asList(refs?.loincMethod).includes(code))
                return itemDef;
        }
        return null;
    }
    /**
     * Every itemDef whose `streamId` is `streamId`, deprecated ones included.
     *
     * A stream can carry more than one item because the same concept is recorded
     * at different fidelities: `symptom-pain-headache` (`activity/plain`, a
     * presence marker for sources that record occurrence only) and
     * `symptom-pain-headache-severity` (`ratio/proportion`, the graded twin) sit
     * on `symptom-pain-headache` together. Data-model 3.2.0 published that
     * contract in prose — the items describe the pairing in their `description`
     * — but nothing links a pair in machine-readable form, so the shared
     * `streamId` is what a consumer has to go on.
     */
    forStreamId(streamId) {
        const res = [];
        for (const [key, data] of Object.entries(this.#model.modelData.items)) {
            if (data?.streamId === streamId)
                res.push(this.forKey(key));
        }
        return res;
    }
    /**
     * get item for a key
     */
    forKey(key, throwErrorIfNotFound = true) {
        if (this.#itemsDefs[key])
            return this.#itemsDefs[key];
        const defData = this.#model.modelData.items[key];
        if (!defData) {
            if (throwErrorIfNotFound)
                throw new Error('Cannot find item definition with key: ' + key);
            return null;
        }
        this.#itemsDefs[key] = new HDSItemDef_ts_1.HDSItemDef(key, defData, this.#model);
        return this.#itemsDefs[key];
    }
    /**
     * get a definition for an event
     *
     * Resolution rule (Plan 46 §2.1 — context-via-substream / D3):
     *   1. Try direct match on every entry in event.streamIds (legacy: events
     *      may carry multiple streamIds, e.g. bridge-athenahealth credentials).
     *   2. If no direct match, walk parents of streamIds[0] looking for a
     *      matching itemDef. Closest ancestor wins.
     *
     * The walk-up reuses `HDSModelStreams.getParentsIds`, the same helper
     * Authorizations consumes (HDSModel-Authorizations.ts:86) and the same
     * algorithm Plan 45's resolveStream.ts uses for clientData lookup.
     */
    forEvent(event, throwErrorIfNotFound = true) {
        const candidates = [];
        for (const streamId of event.streamIds) {
            const keyStreamIdEventType = streamId + ':' + event.type;
            const candidate = this.#modelDataByStreamIdEventTypes[keyStreamIdEventType];
            if (candidate)
                candidates.push(candidate);
        }
        if (candidates.length === 0 && event.streamIds && event.streamIds.length > 0) {
            const primary = event.streamIds[0];
            // getParentsIds returns root-first ancestors of `primary` (excludes self).
            // We need closest-ancestor-wins, so iterate leaf-to-root: walk parents
            // in reverse, then check `primary` is already covered by the direct-match
            // pass above — so we only need ancestors here.
            const ancestorsRootFirst = this.#model.streams.getParentsIds(primary, false);
            for (let i = ancestorsRootFirst.length - 1; i >= 0; i--) {
                const ancestorId = ancestorsRootFirst[i];
                const keyStreamIdEventType = ancestorId + ':' + event.type;
                const candidate = this.#modelDataByStreamIdEventTypes[keyStreamIdEventType];
                if (candidate) {
                    candidates.push(candidate);
                    break;
                }
            }
        }
        if (candidates.length === 0) {
            if (throwErrorIfNotFound)
                throw new Error('Cannot find definition for event: ' + JSON.stringify(event));
            return null;
        }
        if (candidates.length > 1) {
            throw new Error(`Found multiple matching definitions "${candidates.map(c => (c.key)).join(', ')}" for event: ${JSON.stringify(event)}`);
        }
        return this.forKey(candidates[0].key, throwErrorIfNotFound);
    }
}
exports.HDSModelItemsDefs = HDSModelItemsDefs;
/**
 * Add key to model items and
 * load modeldata item into modelDataByStreamIdEventTypes for fast search
 *
 * `streamId:eventType` is the storage identity, and this index is what `forEvent`
 * resolves through — so two *active* items may never share a pair.
 *
 * A **deprecated** item may, and that is what makes an item-key rename non-breaking:
 * the old key stays as a deprecated alias resolvable via `forKey` (so consumers pinned
 * to it keep working and migrate on their own schedule), while the active item owns the
 * pair here, so `forEvent` stays unambiguous. The alias is faithful — same streamId,
 * same eventType — so it produces identical events.
 *
 * This mirrors the loader in `data-model` (`src/items.js`, addItem). The two indexes are
 * separate implementations of the same rule and MUST be kept in step: relaxing one is
 * not relaxing the other. That drift is exactly what caused site-agents#3 — data-model
 * published aliases its own loader accepted, and every hds-lib consumer threw on first
 * `itemsDefs` access.
 */
function loadModelDataByStreamIdEventTypes(model, map) {
    for (const item of Object.values(model)) {
        const it = item;
        const eventTypes = [];
        // Mirror data-model's collection (src/items.js): an item carries EITHER a single
        // `eventType` OR a `variations.eventType` set (e.g. body-weight kg/lb), never both.
        // Check variations first and reject the mixed shape — checking `eventType` first
        // (as this did) silently ignored variations when both were present, and dereferencing
        // `.variations.eventType` in the else branch threw a TypeError when an item had neither.
        if (it.variations?.eventType) {
            eventTypes.push(...it.variations.eventType.options.map((o) => o.value));
            if (it.eventType) {
                throw new Error(`Item "${it.key ?? it.streamId}" mixes eventType and variations.eventType: ${JSON.stringify(it)}`);
            }
        }
        else if (it.eventType) {
            eventTypes.push(it.eventType);
        }
        else {
            throw new Error(`Item "${it.key ?? it.streamId}" has neither eventType nor variations.eventType: ${JSON.stringify(it)}`);
        }
        for (const eventType of eventTypes) {
            const keyStreamIdEventType = item.streamId + ':' + eventType;
            const existing = map[keyStreamIdEventType];
            if (existing) {
                if (!existing.deprecated && !item.deprecated) {
                    throw new Error(`Duplicate streamId + eventType "${keyStreamIdEventType}" for item ${JSON.stringify(item)}`);
                }
                if (existing.deprecated && item.deprecated) {
                    throw new Error(`Two deprecated items share streamId + eventType "${keyStreamIdEventType}" — forEvent would be ambiguous. Keep at most one deprecated alias per pair: ${JSON.stringify(item)}`);
                }
                // Exactly one is active — it owns the index regardless of load order.
                if (item.deprecated)
                    continue;
            }
            map[keyStreamIdEventType] = item;
        }
    }
}
